import "./dashboard.scss";
import { useEffect, useState } from "react";
import { SectionHeader } from "@/sprout/typography";
import { usePage } from "@inertiajs/react";

import ProfileCard from "@components/profile_card";
import RecentlyViewedJobs from "@/components/dashboard/widgets/recently_viewed_jobs_widget";
import JobAlerts from "@/components/dashboard/widgets/job_alerts/widget";
import Tasks from "@/components/dashboard/widgets/tasks_widget";
import Applications from "@/components/dashboard/widgets/applications_widget";
import SearchSection from "@/components/dashboard/search_section.tsx";
import DashboardUploadResumeModal from "@components/dashboard/dashboard_upload_resume_modal";
import { DashboardPreviewProvider } from "@components/dashboard/preview_context";

import useDevice from "@/hooks/use_device";
import useTranslation from "@/hooks/use_translation";
import useProfile from "@/queries/use_profile";
import Fingerprint15 from "../images/fingerprint_15.svg";
import { SurveyCallout } from "@components/survey/survey_callout";
import { useEffectOnce } from "react-use";

export type Subscription = {
  boardName: string;
  filters: string[];
  path: string;
  editPath: string;
  logoUrl: string;
  updatedAt: Date;
};

type PageProps = {
  granularLocationSearchEnabled?: boolean;
  uploadResumeModal?: boolean;
};

type Props = {
  firstName?: string;
  jobBoardRendererDomains?: string[];
  paths: {
    notificationSchedulePath: string;
    recentlyViewedJobsPath: string;
  };
  postToRenderer?: boolean;
  recentlyViewedJobsPerPageDashboard: number;
  newUser: boolean;
  landedDreamJob?: boolean;
  preview?: boolean;
  email?: string;
};

const MobileDashboard = ({ paths, recentlyViewedJobsPerPageDashboard }: Props) => {
  return (
    <>
      <Tasks />
      <Applications />
      <RecentlyViewedJobs
        path={paths.recentlyViewedJobsPath}
        perPage={recentlyViewedJobsPerPageDashboard}
      />
      <JobAlerts />
    </>
  );
};

const DesktopDashboard = ({ paths, recentlyViewedJobsPerPageDashboard }: Props) => {
  return (
    <>
      <div className="dashboard__col1">
        <Tasks />
        <Applications />
      </div>
      <div className="dashboard__col2">
        <ProfileCard />
        <RecentlyViewedJobs
          path={paths.recentlyViewedJobsPath}
          perPage={recentlyViewedJobsPerPageDashboard}
        />
        <JobAlerts />
      </div>
    </>
  );
};

const Dashboard = (props: Props) => {
  const {
    firstName,
    jobBoardRendererDomains = [],
    landedDreamJob,
    newUser,
    postToRenderer,
    preview,
    email,
  } = props;

  const { t } = useTranslation("dashboard");

  const device = useDevice();
  const { granularLocationSearchEnabled, uploadResumeModal } = usePage<PageProps>().props;
  const { data: profile } = useProfile({ mock: preview });
  const [showUploadResumeModal, setShowUploadResumeModal] = useState(false);

  useEffect(() => {
    if (!uploadResumeModal || profile?.resume || localStorage.getItem("uploadResumeModalShown")) {
      return;
    }

    const timer = setTimeout(() => {
      localStorage.setItem("uploadResumeModalShown", "true");
      setShowUploadResumeModal(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [uploadResumeModal, profile?.resume]);

  useEffectOnce(() => {
    if (!postToRenderer) {
      return;
    }

    jobBoardRendererDomains.forEach((jobBoardRendererDomain) => {
      window.opener?.postMessage("loginSuccessful", `https://${jobBoardRendererDomain}`);
    });
  });

  const dashboardTitle = () => {
    if (newUser) {
      return t("header.title_new_user");
    }

    return firstName ? t("header.title_with_name", { name: firstName }) : t("header.title");
  };

  const dashboardSubtitle = () => {
    if (landedDreamJob) {
      return t("header.subtitle_dream_job");
    }

    if (newUser) {
      return t("header.subtitle_new_user");
    }

    const subtitles = t("header.subtitle", { returnObjects: true }) as string[];
    return subtitles[Math.floor(Math.random() * subtitles.length)];
  };

  const content = (
    <>
      <div className="welcome-banner with-search">
        <div className="welcome-container">
          <div className="welcome-text-wrapper">
            <div className="welcome-text">
              <SectionHeader serif large as="h1" className="welcome-title">
                {dashboardTitle()}
              </SectionHeader>
              <SectionHeader className="welcome-subtitle">{dashboardSubtitle()}</SectionHeader>
            </div>
          </div>
          <SearchSection granularLocationSearchEnabled={!!granularLocationSearchEnabled} />
          <img src={Fingerprint15} alt="Welcome Banner Image" className="welcome-banner__image" />
        </div>
      </div>
      <div className="dashboard">
        <SurveyCallout />
        <main>
          {device.isMobile ? <MobileDashboard {...props} /> : <DesktopDashboard {...props} />}
        </main>
      </div>
      <DashboardUploadResumeModal
        open={showUploadResumeModal}
        onOpenChange={setShowUploadResumeModal}
      />
    </>
  );

  if (preview) {
    return <DashboardPreviewProvider email={email ?? ""}>{content}</DashboardPreviewProvider>;
  }

  return content;
};

export default Dashboard;
